import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  LinearProgress,
  Alert,
  TextField,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Checkbox,
  FormControlLabel
} from "@mui/material";

import Navbar from "../Components/Navbar/navbar";
import ListaMateriais, { normalizarImagensMaterial } from "../Components/ListaMateriais/listaMateriais";
import HistoricoMaterial from "../Components/HistoricoMaterial";
import PaginationComponent from "../Components/PaginationComponents/paginationcomponents";
import PaginationSelector from "../Components/PaginationSelector/paginationselector";
import TutorialCadastro from "../Components/TutorialCadastro/TutorialCadastro";
import { ETAPAS_TUTORIAL_MATERIAIS } from "../Components/TutorialCadastro/etapasTutoriais";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  podeEditarCadastroDireto,
  solicitarAprovacaoCadastro,
} from "../Utils/aprovacaoCadastro";

/* FIREBASE */
import { db, auth, storage } from "../Config/firebase";
import {
  collection,
  getDocs,
  query,
  doc,
  updateDoc,
  Timestamp,
  limit,
  where
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

/* FUNCTIONS */
import { functions } from "../Config/firebase";
import { httpsCallable } from "firebase/functions";

import app from "../Config/firebase";

/* HISTÓRICO */
import { registrarHistorico } from "../Utils/registrarHistorico";
import { aplicarTenant, getUsuarioAtualComTenant, tenantWhere } from "../Utils/tenant";
import { compressImage } from "../../utils/compressImage";
import RelatorioEntradaMaterialPrint from "./RelatorioEntradaMaterialPrint";
import {
  atualizarDocumentoComMesclagem,
  extrairCamposAlterados,
} from "../Utils/concorrenciaFirestore";

import "./cadastromateriais.css";

const OPCOES_AGREGADO_GRAUDO = [
  "BRITA 0",
  "BRITA 1",
  "BRITA 2",
  "BRITA 3",
  "BRITA 4",
  "SEIXO 1",
  "SEIXO 0",
];
const OPCOES_AGREGADO_MIUDO = ["AREIA NATURAL", "AREIA ARTIFICIAL", "CAL"];
const TODAS_OPCOES_DESCRICAO_AGREGADO = [
  ...OPCOES_AGREGADO_GRAUDO,
  ...OPCOES_AGREGADO_MIUDO,
];

function identificarOpcaoDescricaoAgregado(descricao = "") {
  const texto = String(descricao).trim().toUpperCase();
  return (
    TODAS_OPCOES_DESCRICAO_AGREGADO.find(
      (opcao) => texto === opcao || texto.startsWith(`${opcao} `)
    ) || ""
  );
}

function aplicarOpcaoNaDescricao(descricao = "", opcao = "", opcaoAnterior = "") {
  const opcaoAtual = opcaoAnterior || identificarOpcaoDescricaoAgregado(descricao);
  const complemento = opcaoAtual
    ? String(descricao).trim().slice(opcaoAtual.length).trim()
    : String(descricao).trim();
  return [opcao, complemento].filter(Boolean).join(" ");
}

const TODAS_OBRAS_ID = "__todas_obras__";
const OPCAO_TODAS_OBRAS = {
  id: TODAS_OBRAS_ID,
  nome: "Todas as obras (todos os clientes)"
};

const CATEGORIAS_MATERIAL = [
  { value: "solos", label: "Solos" },
  { value: "asfalto", label: "Asfalto" },
  { value: "agregado", label: "Agregado" },
  { value: "concreto", label: "Concreto" },
  { value: "diversos", label: "Diversos" },
];

function categoriaMaterialCadastrada(material = {}) {
  if (material.IsAgregadoGraudo || material.IsAgregadoMiudo) return "agregado";
  if (material.CategoriaMaterial) return material.CategoriaMaterial;
  if (material.IsCAP || material.IsEmulsao || material.IsDOP) return "asfalto";
  if (material.IsSolos) return "solos";
  if (material.IsCimento || material.IsAditivo || material.IsAdicao || material.IsAgua) return "concreto";
  return "";
}

function normalizarTextoBusca(valor) {
  return String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function separarCodigoMaterial(valor) {
  const [base = "", ...complemento] = String(valor ?? "").split(".");
  return {
    base,
    complemento: complemento.join("."),
  };
}

function montarCodigoMaterial(base, complemento) {
  const baseNormalizada = String(base || "").trim();
  const complementoNormalizado = String(complemento || "").trim().toUpperCase();
  if (!baseNormalizada) return "";
  return complementoNormalizado
    ? `${Number(baseNormalizada)}.${complementoNormalizado}`
    : Number(baseNormalizada);
}

function compararCodigosMateriaisDecrescente(materialA, materialB) {
  const codigoA = separarCodigoMaterial(materialA?.codigo);
  const codigoB = separarCodigoMaterial(materialB?.codigo);
  const baseA = Number(codigoA.base);
  const baseB = Number(codigoB.base);

  if (baseA !== baseB) return baseB - baseA;

  if (codigoA.complemento && !codigoB.complemento) return -1;
  if (!codigoA.complemento && codigoB.complemento) return 1;

  return codigoB.complemento.localeCompare(codigoA.complemento, "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });
}

function formatarDataBusca(dataCadastro) {
  if (!dataCadastro?.seconds) return "";

  const data = new Date(dataCadastro.seconds * 1000);
  const dia = String(data.getUTCDate()).padStart(2, "0");
  const mes = String(data.getUTCMonth() + 1).padStart(2, "0");
  const ano = data.getUTCFullYear();

  return `${dia}/${mes}/${ano} ${ano}-${mes}-${dia}`;
}

function filtrarMateriais(lista, termoPesquisa, statusSelecionado, mapaObras) {
  const termoBusca = normalizarTextoBusca(termoPesquisa);
  return (lista || []).filter((material) => {
    const obraNome = mapaObras[material.ObraId] || "";
    const conteudoBusca = normalizarTextoBusca([
      material.codigo,
      formatarDataBusca(material.DataCadastro),
      material.Cliente,
      obraNome,
      material.Fornecedor,
      material.Descricao,
    ].join(" "));

    return conteudoBusca.includes(termoBusca) &&
      (statusSelecionado === "todos" || material.Status === statusSelecionado);
  });
}

function dataCadastroParaInput(valor) {
  if (!valor) return "";

  if (typeof valor === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor;
    const dataString = new Date(valor);
    return Number.isNaN(dataString.getTime()) ? "" : dataString.toISOString().slice(0, 10);
  }

  const data =
    valor?.seconds ? new Date(valor.seconds * 1000) :
    valor?.toDate ? valor.toDate() :
    valor instanceof Date ? valor :
    null;

  return data && !Number.isNaN(data.getTime()) ? data.toISOString().slice(0, 10) : "";
}

function timestampDataCadastro(valor) {
  const dataInput = dataCadastroParaInput(valor);
  if (!dataInput) {
    throw new Error("Data de cadastro inválida.");
  }

  const [ano, mes, dia] = dataInput.split("-").map(Number);
  const data = new Date(ano, mes - 1, dia);

  if (Number.isNaN(data.getTime())) {
    throw new Error("Data de cadastro inválida.");
  }

  return Timestamp.fromDate(data);
}

export default function CadastroMateriais({ open, isMobile }) {

  /* =========================
     STATES
  ========================= */
  const [materiais, setMateriais] = useState([]);
  const [materiaisBusca, setMateriaisBusca] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [avisoAprovacao, setAvisoAprovacao] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [obraId, setObraId] = useState("");
  const [obras, setObras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [fornecedor, setFornecedor] = useState("");
  const [localColeta, setLocalColeta] = useState("");
  const [entregueCliente, setEntregueCliente] = useState(false);
  const [colaboradorColeta, setColaboradorColeta] = useState("");
  const [mapaObras, setMapaObras] = useState({});
  const [isCimento, setIsCimento] = useState(false);
  const [isAditivo, setIsAditivo] = useState(false);
  const [isAdicao, setIsAdicao] = useState(false);
  const [isAgua, setIsAgua] = useState(false);
  const [isAgregadoGraudo, setIsAgregadoGraudo] = useState(false);
  const [isAgregadoMiudo, setIsAgregadoMiudo] = useState(false);
  const [isSolos, setIsSolos] = useState(false);
  const [isCAP, setIsCAP] = useState(false);
  const [isEmulsao, setIsEmulsao] = useState(false);
  const [isDOP, setIsDOP] = useState(false);
  const [categoriaMaterial, setCategoriaMaterial] = useState("");
  const [massaEspecifica, setMassaEspecifica] = useState("");
  const [imagensMaterial, setImagensMaterial] = useState([]);
  const [imagensRemovidas, setImagensRemovidas] = useState([]);
  const [progressoImagens, setProgressoImagens] = useState(0);
  const [showImagens, setShowImagens] = useState(false);
  const [imagensVisualizacao, setImagensVisualizacao] = useState([]);
  const [usuarioAtual, setUsuarioAtual] = useState(null);


  /* PAGINAÇÃO */
  const [currentPage, setCurrentPage] = useState(0);
  const [itensPerPage, setItensPerPage] = useState(20);
  const [pages, setPages] = useState(1);

  /* MODAIS */
  const [showModal, setShowModal] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [idEditando, setIdEditando] = useState(null);

  const [showHistorico, setShowHistorico] = useState(false);
  const [materialHistorico, setMaterialHistorico] = useState(null);

  /* PESQUISA */
  const [buscaDigitada, setBuscaDigitada] = useState("");
      const [busca, setBusca] = useState("");
      const [buscaDebounce, setBuscaDebounce] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [relatorioEntradaMaterial, setRelatorioEntradaMaterial] = useState(null);

  /* FORM */
  const [codigo, setCodigo] = useState("");
  const [complementoCodigo, setComplementoCodigo] = useState("");
  const [openTutorial, setOpenTutorial] = useState(false);
  const [dataCadastro, setDataCadastro] = useState("");
  const [cliente, setCliente] = useState("");
  const [descricao, setDescricao] = useState("");
  const [opcaoDescricaoAgregado, setOpcaoDescricaoAgregado] = useState("");
  const [popupTipoAgregado, setPopupTipoAgregado] = useState("");
  const [novoAgregado, setNovoAgregado] = useState("");
  const [ensaios, setEnsaios] = useState("");
  const [status, setStatus] = useState("nao_iniciado");

  const cadastrarMaterial = httpsCallable(functions, "cadastrarMaterial");



  async function carregarTodasObras() {
  const snap = await getDocs(query(collection(db, "obras"), ...tenantWhere(usuarioAtual)));

  const mapa = {};

  mapa[TODAS_OBRAS_ID] = "Todas as obras (todos os clientes)";

  snap.docs.forEach(doc => {
    mapa[doc.id] = doc.data().nome;
  });

  setMapaObras(mapa);
}
  /* =========================
     BUSCA COMPLETA
  ========================= */
    async function carregarClientes() {
      const snap = await getDocs(query(collection(db, "clientes"), ...tenantWhere(usuarioAtual)));

      return snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }

    async function carregarObras(clienteId) {
  const snap = await getDocs(
    query(
      collection(db, "obras"),
      ...tenantWhere(usuarioAtual),
      where("clienteId", "==", clienteId)
    )
  );

  setObras(
    snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  );
}


  async function carregarTodosParaBusca(forcarAtualizacao = false) {
    if (materiaisBusca && !forcarAtualizacao) return materiaisBusca;

    setLoading(true);
    const snap = await getDocs(
      query(
        collection(db, "tblListaMateriaisCadastrados"),
        ...tenantWhere(usuarioAtual)
      )
    );

    const listaCompleta = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort(compararCodigosMateriaisDecrescente);
    setMateriaisBusca(listaCompleta);
    setLoading(false);
    return listaCompleta;
  }

  /* =========================
     CARREGAR LISTA
  ========================= */
  async function carregar(page = 0) {
  try {
    setLoading(true);

    const q = query(
      collection(db, "tblListaMateriaisCadastrados"),
      ...tenantWhere(usuarioAtual)
    );

    const snap = await getDocs(q);
    const todosMateriais = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort(compararCodigosMateriaisDecrescente);
    const inicioPagina = page * itensPerPage;
    const listaMateriais = todosMateriais.slice(
      inicioPagina,
      inicioPagina + itensPerPage
    );

   /* 🔥 BUSCAR APROVAÇÕES PENDENTES */
let pendenciasPorMaterial = new Map();

try {
  const snapPendencias = await getDocs(
    query(
      collection(db, "aprovacoes"),
      ...tenantWhere(usuarioAtual),
      where("status", "==", "pendente")
    )
  );

  snapPendencias.docs.forEach((documento) => {
    const pendencia = documento.data();
    const materialId = pendencia.registroId || pendencia.materialId;
    const ehCadastroMaterial =
      pendencia.colecao === "tblListaMateriaisCadastrados" || Boolean(pendencia.materialId);

    if (materialId && ehCadastroMaterial) {
      pendenciasPorMaterial.set(materialId, pendencia);
    }
  });

} catch (err) {
  console.error("Erro ao buscar pendências:", err);
}

    /* 🔥 MARCAR FLAG */
    const listaComPendencia = listaMateriais.map((material) => {
      const pendencia = pendenciasPorMaterial.get(material.id);
      const dadosPendentes = pendencia?.dadosSolicitados || {};
      const possuiAlteracaoDeImagens = Object.prototype.hasOwnProperty.call(
        dadosPendentes,
        "ImagensMaterial"
      );

      return {
        ...material,
        ...(possuiAlteracaoDeImagens
          ? { ImagensMaterial: normalizarImagensMaterial(dadosPendentes) }
          : {}),
        edicaoPendente: Boolean(pendencia),
      };
    });

    setMateriais(listaComPendencia);

    setPages(Math.max(1, Math.ceil(todosMateriais.length / itensPerPage)));

  } catch (err) {
    console.error(err);
    setErro("Erro ao carregar materiais.");
  } finally {
    setLoading(false);
  }
}


  useEffect(() => {
    if (usuarioAtual) carregar(currentPage);
  }, [currentPage, itensPerPage, usuarioAtual]);

  useEffect(() => {
    async function carregarUsuario() {
      if (!auth.currentUser) return;
      setUsuarioAtual(await getUsuarioAtualComTenant());
    }

    carregarUsuario();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("novo") === "1") {
      abrirCadastro();
    }
  }, []);

useEffect(() => {
  if (usuarioAtual) carregarTodasObras();
}, [usuarioAtual]);

  useEffect(() => {
  resetPaginacao();
}, [itensPerPage]);

useEffect(() => {
  async function aplicarBusca() {
    if (buscaDebounce) {
      await carregarTodosParaBusca();
    }
    setBusca(buscaDebounce);
    resetPaginacao();
  }

  aplicarBusca();
}, [buscaDebounce]);

useEffect(() => {
  const timer = setTimeout(() => {
    setBuscaDebounce(buscaDigitada);
  }, 400);

  return () => clearTimeout(timer);
}, [buscaDigitada]);

  function resetPaginacao() {
    setCurrentPage(0);
  }

  async function recarregarAposAlteracao(page = currentPage) {
    setMateriaisBusca(null);
    await carregar(page);

    if (buscaDebounce) {
      await carregarTodosParaBusca(true);
    }
  }

  /* =========================
     AÇÕES
  ========================= */
  async function abrirCadastro() {
       
    const listaClientes = await carregarClientes();
   setClientes(listaClientes);
   setClienteId("");
    setObraId("");
    setObras([]);
    setAvisoAprovacao("");
    setModoEdicao(false);
    setIdEditando(null);
    setCodigo("");
    setComplementoCodigo("");
    setCliente("");
    setFornecedor("");
    setDescricao("");
    setOpcaoDescricaoAgregado("");
    setPopupTipoAgregado("");
    setNovoAgregado("");
    setEnsaios("");    
    setLocalColeta("");
    setEntregueCliente(false);
    setColaboradorColeta("");
    setIsCimento(false);
    setIsAditivo(false);
    setIsAdicao(false);
    setIsAgua(false);
    setIsAgregadoGraudo(false);
    setIsAgregadoMiudo(false);
    setIsSolos(false);
    setIsCAP(false);
    setIsEmulsao(false);
    setIsDOP(false);
    setCategoriaMaterial("");
    setMassaEspecifica("");
    setImagensMaterial([]);
    setImagensRemovidas([]);
    setProgressoImagens(0);
    setStatus("nao_iniciado");
    setDataCadastro(new Date().toISOString().slice(0,10));
    setShowModal(true);

  }

          async function abrirEdicao(m) {
           setAvisoAprovacao("");

          // 🔥 CARREGAR CLIENTES
            const listaClientes = await carregarClientes();
            setClientes(listaClientes);

            setModoEdicao(true);
            setIdEditando(m.id);
            const codigoMaterial = separarCodigoMaterial(m.codigo);
            setCodigo(codigoMaterial.base);
            setComplementoCodigo(codigoMaterial.complemento);

            setCliente(m.Cliente || "");
            setClienteId(m.ClienteId || "");

            setObraId(m.TodasObras === true ? TODAS_OBRAS_ID : (m.ObraId || ""));
            setFornecedor(m.Fornecedor || "");
            setDescricao(m.Descricao || "");
            setOpcaoDescricaoAgregado(
              m.DescricaoAgregado || identificarOpcaoDescricaoAgregado(m.Descricao || "")
            );
            setPopupTipoAgregado("");
            setNovoAgregado("");
            setEnsaios(m.Ensaios || "");
            setLocalColeta(m.LocalColeta || "");
            setEntregueCliente(m.EntregueCliente || false);
            setColaboradorColeta(m.ColaboradorColeta || "");
            setIsCimento(Boolean(m.IsCimento));
            setIsAditivo(Boolean(m.IsAditivo));
            setIsAdicao(Boolean(m.IsAdicao));
            setIsAgua(Boolean(m.IsAgua));
            setIsAgregadoGraudo(Boolean(m.IsAgregadoGraudo));
            setIsAgregadoMiudo(Boolean(m.IsAgregadoMiudo));
            setIsSolos(Boolean(m.IsSolos));
            setIsCAP(Boolean(m.IsCAP));
            setIsEmulsao(Boolean(m.IsEmulsao));
            setIsDOP(Boolean(m.IsDOP));
            setCategoriaMaterial(categoriaMaterialCadastrada(m));
            setMassaEspecifica(m.MassaEspecifica || "");
            setImagensMaterial(normalizarImagensMaterial(m));
            setImagensRemovidas([]);
            setProgressoImagens(0);
            setStatus(m.Status);

            // 🔥 CARREGAR OBRAS DO CLIENTE
            if (m.ClienteId) {
              await carregarObras(m.ClienteId);
            }

            setDataCadastro(dataCadastroParaInput(m.DataCadastro));

            setShowModal(true);
          }

  async function clonarMaterial(material) {
    await abrirEdicao(material);
    setModoEdicao(false);
    setIdEditando(null);
    setCodigo("");
    setComplementoCodigo("");
  }

  function abrirHistorico(material) {
    setMaterialHistorico(material);
    setShowHistorico(true);
  }

  function abrirImagens(material) {
    setImagensVisualizacao(normalizarImagensMaterial(material));
    setShowImagens(true);
  }

  function selecionarImagensMaterial(event) {
    const arquivos = Array.from(event.target.files || []).filter((arquivo) => arquivo.type.startsWith("image/"));
    if (!arquivos.length) return;

    setImagensMaterial((atuais) => [
      ...atuais,
      ...arquivos.map((arquivo, index) => ({
        id: `nova-${Date.now()}-${index}`,
        arquivo,
        nome: arquivo.name,
        previewUrl: URL.createObjectURL(arquivo),
      })),
    ]);
    event.target.value = "";
  }

  function removerImagemMaterial(index) {
    const imagem = imagensMaterial[index];
    if (imagem?.path) setImagensRemovidas((paths) => [...paths, imagem.path]);
    if (imagem?.previewUrl) URL.revokeObjectURL(imagem.previewUrl);
    setImagensMaterial((atuais) => atuais.filter((_, itemIndex) => itemIndex !== index));
  }

  async function enviarImagensMaterial(materialId, userData) {
    const novas = imagensMaterial.filter((imagem) => imagem.arquivo);
    let concluidas = 0;
    const finais = [];

    for (let index = 0; index < imagensMaterial.length; index += 1) {
      const imagem = imagensMaterial[index];
      if (!imagem.arquivo) {
        finais.push({ id: imagem.id || `imagem-${index + 1}`, nome: imagem.nome || "", path: imagem.path || "", url: imagem.url || "" });
        continue;
      }

      const comprimida = await compressImage(imagem.arquivo, { maxWidth: 1600, maxHeight: 1600, quality: 0.78 });
      const idImagem = `imagem-${Date.now()}-${index + 1}`;
      const path = `tenants/${userData.tenantId}/materiais/${materialId}/imagens/${idImagem}.jpg`;
      const storageRef = ref(storage, path);

      await new Promise((resolve, reject) => {
        const tarefa = uploadBytesResumable(storageRef, comprimida, { contentType: "image/jpeg" });
        tarefa.on("state_changed", (snapshot) => {
          const fracao = snapshot.totalBytes ? snapshot.bytesTransferred / snapshot.totalBytes : 0;
          setProgressoImagens(Math.round(((concluidas + fracao) / Math.max(1, novas.length)) * 100));
        }, reject, resolve);
      });

      finais.push({ id: idImagem, nome: imagem.nome || imagem.arquivo.name, path, url: await getDownloadURL(storageRef) });
      concluidas += 1;
      setProgressoImagens(Math.round((concluidas / Math.max(1, novas.length)) * 100));
    }

    return finais;
  }

  async function excluirImagensRemovidas() {
    await Promise.all(imagensRemovidas.map((path) => deleteObject(ref(storage, path)).catch(() => {})));
  }

  function limparTiposMaterial() {
    setIsCimento(false);
    setIsAditivo(false);
    setIsAdicao(false);
    setIsAgua(false);
    setIsAgregadoGraudo(false);
    setIsAgregadoMiudo(false);
    setIsSolos(false);
    setIsCAP(false);
    setIsEmulsao(false);
    setIsDOP(false);
  }

  function selecionarCategoriaMaterial(categoria) {
    limparTiposMaterial();
    setCategoriaMaterial(categoria);
    setOpcaoDescricaoAgregado("");
    if (categoria === "solos") setIsSolos(true);
  }

  function selecionarTipoMaterial(tipo, marcado) {
    if (!marcado) {
      if (tipo === "cimento") setIsCimento(false);
      if (tipo === "aditivo") setIsAditivo(false);
      if (tipo === "adicao") setIsAdicao(false);
      if (tipo === "agua") setIsAgua(false);
      if (tipo === "agregadoGraudo") {
        setIsAgregadoGraudo(false);
        setOpcaoDescricaoAgregado("");
      }
      if (tipo === "agregadoMiudo") {
        setIsAgregadoMiudo(false);
        setOpcaoDescricaoAgregado("");
      }
      if (tipo === "solos") setIsSolos(false);
      if (tipo === "cap") setIsCAP(false);
      if (tipo === "emulsao") setIsEmulsao(false);
      if (tipo === "dop") setIsDOP(false);
      return;
    }

    limparTiposMaterial();
    if (tipo === "cimento") {
      setIsCimento(true);
      setOpcaoDescricaoAgregado("");
    }
    if (tipo === "aditivo") {
      setIsAditivo(true);
      setOpcaoDescricaoAgregado("");
    }
    if (tipo === "adicao") {
      setIsAdicao(true);
      setOpcaoDescricaoAgregado("");
    }
    if (tipo === "agua") {
      setIsAgua(true);
      setOpcaoDescricaoAgregado("");
      if (!massaEspecifica) setMassaEspecifica("1,000");
    }
    if (tipo === "agregadoGraudo") {
      setIsAgregadoGraudo(true);
      setNovoAgregado("");
      setPopupTipoAgregado("graudo");
    }
    if (tipo === "agregadoMiudo") {
      setIsAgregadoMiudo(true);
      setNovoAgregado("");
      setPopupTipoAgregado("miudo");
    }
    if (tipo === "solos") {
      setIsSolos(true);
      setOpcaoDescricaoAgregado("");
    }
    if (tipo === "cap") {
      setIsCAP(true);
      setOpcaoDescricaoAgregado("");
    }
    if (tipo === "emulsao") {
      setIsEmulsao(true);
      setOpcaoDescricaoAgregado("");
    }
    if (tipo === "dop") {
      setIsDOP(true);
      setOpcaoDescricaoAgregado("");
    }
  }

  function selecionarOpcaoDescricaoAgregado(opcao) {
    setDescricao((atual) =>
      aplicarOpcaoNaDescricao(atual, opcao, opcaoDescricaoAgregado)
    );
    setOpcaoDescricaoAgregado(opcao);
    setNovoAgregado("");
    setPopupTipoAgregado("");
  }

  function adicionarAgregadoManual() {
    const opcao = String(novoAgregado).trim().toUpperCase();
    if (!opcao) return;
    selecionarOpcaoDescricaoAgregado(opcao);
  }

  function atualizarDescricaoMaterial(valor) {
    if (!opcaoDescricaoAgregado) {
      setDescricao(valor);
      return;
    }

    const descricaoInformada = String(valor).trimStart().toUpperCase();
    if (
      descricaoInformada === opcaoDescricaoAgregado ||
      descricaoInformada.startsWith(`${opcaoDescricaoAgregado} `)
    ) {
      setDescricao(valor);
      return;
    }

    setDescricao([opcaoDescricaoAgregado, String(valor).trim()].filter(Boolean).join(" "));
  }

  /* =========================
     ALTERAR STATUS (COM HISTÓRICO)
  ========================= */
  async function alterarStatus(id, novoStatus) {
    try {
      const user = auth.currentUser;
      const userData = await getUsuarioAtualComTenant();

      const antigo = materiais.find(m => m.id === id);

      const dados = {
        Status: novoStatus,
        updatedAt: Timestamp.now(),
        updatedBy: {
          uid: user.uid,
          nome: userData.nome,
          email: user.email || userData.email || "",
          tipo: userData.tipo
        }
      };

      if (podeEditarCadastroDireto(userData, antigo)) {
        await updateDoc(doc(db, "tblListaMateriaisCadastrados", id), dados);
      } else {
        await solicitarAprovacaoCadastro({
          usuario: userData,
          registro: antigo,
          colecao: "tblListaMateriaisCadastrados",
          registroId: id,
          dados: { Status: novoStatus },
          tipo: "cadastro_material",
          titulo: `Alteração do material ${antigo?.codigo || ""}`,
        });
        setAvisoAprovacao("Alteração enviada para aprovação do administrador.");
        return;
      }

      await registrarHistorico({
        materialId: id,
        codigo: antigo.codigo,
        acao: "ALTERACAO_STATUS",
        campo: "Status",
        valorAnterior: antigo.Status,
        valorNovo: novoStatus,
        usuario: dados.updatedBy
      });

      setMateriais(prev =>
        prev.map(m => (m.id === id ? { ...m, Status: novoStatus } : m))
      );
      setMateriaisBusca(prev =>
        prev ? prev.map(m => (m.id === id ? { ...m, Status: novoStatus } : m)) : prev
      );
    } catch {
      setErro("Erro ao alterar status.");
    }
  }

  /* =========================
     SALVAR (CRIAÇÃO + EDIÇÃO COM HISTÓRICO)
  ========================= */
  async function salvar() {
  try {
    setSalvando(true);
    setErro("");
    setProgressoImagens(0);

   

      if (!dataCadastro || !clienteId || !obraId || !fornecedor.trim() || !descricao.trim()) {
        if (!entregueCliente && !colaboradorColeta.trim()) {
          setErro("Informe o colaborador que realizou a coleta.");
          setSalvando(false);
          return;
        }
          setErro("Todos os campos são de preenchimento obrigatório.");
          setSalvando(false);
          return;
        }

      if ((isAgregadoGraudo || isAgregadoMiudo) && !opcaoDescricaoAgregado) {
        setErro("Selecione a descrição correspondente ao tipo de agregado.");
        setSalvando(false);
        return;
      }

      if (!categoriaMaterial) {
        setErro("Selecione a categoria do material.");
        setSalvando(false);
        return;
      }

      if (!["solos", "diversos"].includes(categoriaMaterial) && !(isCimento || isAditivo || isAdicao || isAgua || isAgregadoGraudo || isAgregadoMiudo || isCAP || isEmulsao || isDOP)) {
        setErro("Selecione o tipo do material.");
        setSalvando(false);
        return;
      }

    const user = auth.currentUser;
    const userData = await getUsuarioAtualComTenant();

    if (!userData.tenantId) {
      setErro("Usuario sem tenantId. Use um admin de cliente SaaS para cadastros operacionais.");
      setSalvando(false);
      return;
    }

    const codigoBaseNormalizado = String(codigo || "").trim();
    const complementoNormalizado = String(complementoCodigo || "").trim().toUpperCase();

    if (codigoBaseNormalizado && !/^\d+$/.test(codigoBaseNormalizado)) {
      setErro("O código-base deve conter somente números.");
      setSalvando(false);
      return;
    }

    if (complementoNormalizado && !/^[A-Z0-9]{1,5}$/.test(complementoNormalizado)) {
      setErro("O complemento deve conter até 5 letras ou números, sem pontos ou espaços.");
      setSalvando(false);
      return;
    }

    if (!modoEdicao && Boolean(codigoBaseNormalizado) !== Boolean(complementoNormalizado)) {
      setErro("Para encaixar um código, informe o código-base e o complemento.");
      setSalvando(false);
      return;
    }

    if (modoEdicao && !codigoBaseNormalizado) {
      setErro("Informe o código-base do material.");
      setSalvando(false);
      return;
    }

    const codigoFinal = montarCodigoMaterial(codigoBaseNormalizado, complementoNormalizado);
    const tipoComMassaEspecifica = isCimento || isAditivo || isAdicao || isAgua;
    const massaEspecificaInformada = String(massaEspecifica).trim();
    const massaEspecificaNumerica = Number(massaEspecificaInformada.replace(",", "."));

    if (
      tipoComMassaEspecifica &&
      (!massaEspecificaInformada || !Number.isFinite(massaEspecificaNumerica))
    ) {
      setErro("Informe uma massa específica válida.");
      setSalvando(false);
      return;
    }

    const massaEspecificaFormatada = tipoComMassaEspecifica
      ? massaEspecificaNumerica.toFixed(3).replace(".", ",")
      : "";

 const payload = aplicarTenant({
  Cliente: cliente,
  ClienteId: clienteId,
  ObraId: obraId,
  Fornecedor: fornecedor,
  LocalColeta: localColeta,
  EntregueCliente: entregueCliente,
  ColaboradorColeta: entregueCliente ? "" : colaboradorColeta,
  Descricao: descricao,
  DescricaoAgregado: isAgregadoGraudo || isAgregadoMiudo ? opcaoDescricaoAgregado : "",
  Ensaios: ensaios,
  IsCimento: isCimento,
  IsAditivo: isAditivo,
  IsAdicao: isAdicao,
  IsAgua: isAgua,
  IsAgregadoGraudo: isAgregadoGraudo,
  IsAgregadoMiudo: isAgregadoMiudo,
  IsSolos: isSolos,
  IsCAP: isCAP,
  IsEmulsao: isEmulsao,
  IsDOP: isDOP,
  CategoriaMaterial: categoriaMaterial,
  TodasObras: obraId === TODAS_OBRAS_ID,
  MassaEspecifica: massaEspecificaFormatada,
  Status: status,
  DataCadastro: timestampDataCadastro(dataCadastro),
  createdAt: Timestamp.now(),
  updatedAt: Timestamp.now(),
  updatedBy: {
    uid: user.uid,
    nome: userData.nome,
    email: user.email || userData.email || "",
    tipo: userData.tipo
  },
  ...(modoEdicao ? { codigo: codigoFinal } : {})
}, userData);
    /* =========================
       🔥 EDIÇÃO
    ========================= */
    if (modoEdicao) {
      const materialOriginal =
        materiais.find((material) => material.id === idEditando) ||
        materiaisBusca?.find((material) => material.id === idEditando) ||
        {};

      if (String(materialOriginal.codigo) !== String(codigoFinal)) {
        const codigoExistente = await getDocs(
          query(
            collection(db, "tblListaMateriaisCadastrados"),
            ...tenantWhere(userData),
            where("codigo", "==", codigoFinal),
            limit(1)
          )
        );

        if (codigoExistente.docs.some((material) => material.id !== idEditando)) {
          setErro(`O código ${codigoFinal} já está sendo utilizado.`);
          return;
        }

      }

      const imagensFinais = await enviarImagensMaterial(idEditando, userData);
      const payloadComImagens = { ...payload, ImagensMaterial: imagensFinais };

      if (podeEditarCadastroDireto(userData, materialOriginal)) {
        await atualizarDocumentoComMesclagem({
          db,
          referencia: doc(db, "tblListaMateriaisCadastrados", idEditando),
          original: materialOriginal,
          atual: { ...materialOriginal, ...payloadComImagens },
          metadados: {
            updatedAt: payloadComImagens.updatedAt,
            updatedBy: payloadComImagens.updatedBy,
          },
        });
        await excluirImagensRemovidas();

        await registrarHistorico({
            materialId: idEditando,
          codigo: codigoFinal,
          acao: "EDICAO",
          usuario: payload.updatedBy
        });

        setShowModal(false);
        await recarregarAposAlteracao(currentPage);

      } else {
        await solicitarAprovacaoCadastro({
          usuario: userData,
          registro: materialOriginal,
          colecao: "tblListaMateriaisCadastrados",
          registroId: idEditando,
          dados: extrairCamposAlterados(materialOriginal, {
            ...materialOriginal,
            ...payloadComImagens,
          }),
          tipo: "cadastro_material",
          titulo: `Alteração do material ${codigoFinal || ""}`,
        });

        setAvisoAprovacao("Alteração enviada para aprovação do administrador.");
        return;
      }

    } 
    /* =========================
       🔥 CRIAÇÃO
    ========================= */
    else {
  const respostaCadastro = await cadastrarMaterial({
    ...payload,
    codigoBaseComplementar: codigoBaseNormalizado || null,
    complementoCodigo: complementoNormalizado || null,
  });
  const materialId = respostaCadastro?.data?.materialId;
  if (!materialId) throw new Error("O cadastro foi criado, mas não retornou o identificador do material.");

  const imagensFinais = await enviarImagensMaterial(materialId, userData);
  if (imagensFinais.length > 0) {
    await updateDoc(doc(db, "tblListaMateriaisCadastrados", materialId), aplicarTenant({
      ImagensMaterial: imagensFinais,
      updatedAt: Timestamp.now(),
      updatedBy: payload.updatedBy,
    }, userData));
  }

  setShowModal(false);
  resetPaginacao();
  await recarregarAposAlteracao(0);
}

  } catch (err) {
    console.error("Erro real:", err);
    setErro(err.message || "Erro ao salvar material.");
  } finally {
    setSalvando(false);
  }
}


  /* =========================
     FILTRO
  ========================= */
  const materiaisFiltrados = useMemo(() => {
    const base = buscaDebounce ? (materiaisBusca ?? []) : materiais;
    return filtrarMateriais(base, buscaDebounce, filtroStatus, mapaObras);
  }, [materiais, materiaisBusca, buscaDebounce, filtroStatus, mapaObras]);

  async function abrirRelatorioEntradaMaterial() {
    const listaCompleta = await carregarTodosParaBusca();
    setRelatorioEntradaMaterial({
      materiais: filtrarMateriais(listaCompleta, buscaDigitada, filtroStatus, mapaObras),
      pesquisa: buscaDigitada.trim(),
      status: filtroStatus,
    });
  }

  if (relatorioEntradaMaterial) {
    return <RelatorioEntradaMaterialPrint materiais={relatorioEntradaMaterial.materiais} mapaObras={mapaObras} filtroPesquisa={relatorioEntradaMaterial.pesquisa} filtroStatus={relatorioEntradaMaterial.status} onVoltar={() => setRelatorioEntradaMaterial(null)} />;
  }

  /* =========================
     RENDER
  ========================= */
  return (
    <>
      <Navbar open={open} isMobile={isMobile} />

      <Box sx={{ p: 2, mt: 2 }}>
        <Paper
          className="cabecalho-lista-cadastro"
          elevation={0}
          sx={{
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
            mb: 2,
            p: 2,
          }}
        >
        <Typography variant="h5" mb={1}>RQ 4.7 Entrada de material</Typography>  

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
  size="small"
  variant="outlined"
  onClick={abrirCadastro}
  sx={{
    whiteSpace: "nowrap",
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 500
  }}
>
  + Cadastrar
</Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={abrirRelatorioEntradaMaterial}
            disabled={loading}
            sx={{ whiteSpace: "nowrap", textTransform: "none" }}
          >
            Exportar PDF
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setOpenTutorial(true)}
            sx={{ whiteSpace: "nowrap", textTransform: "none" }}
          >
            Ver tutorial
          </Button>

          <TextField
            label="Pesquisar"
            value={buscaDigitada}
            onChange={(e) => {
              setBuscaDigitada(e.target.value);
            }}
            sx={{ flex: 1 }}
            size="small"
          />

          <TextField
              select
              label="Status"
              size="small"
              value={filtroStatus}
              onChange={e => setFiltroStatus(e.target.value)}
              sx={{
                width: 180,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: "1px"
                  }
                }
              }}
            >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="nao_iniciado">Não iniciado</MenuItem>
            <MenuItem value="em_andamento">Em andamento</MenuItem>
            <MenuItem value="concluido">Concluído</MenuItem>
          </TextField>
        </Stack>
        </Paper>

        <Paper sx={{ height: "calc(100vh - 200px)" }}>
            {loading ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                }}
              >
                <CircularProgress />
                <Typography color="text.secondary">Carregando materiais...</Typography>
              </Box>
            ) : (
              <ListaMateriais
                arrayEnsaios={buscaDebounce ? materiaisFiltrados : materiais}
                editar={abrirEdicao}
                clonar={clonarMaterial}
                alterarStatus={alterarStatus}
                abrirHistorico={abrirHistorico}
                abrirImagens={abrirImagens}
                mapaObras={mapaObras} // 🔥 ADICIONAR ESTA LINHA
              />
            )}
       
        </Paper>

        {!loading && !buscaDebounce.trim() && (
          <Stack direction="row" justifyContent="space-between" mt={2}>
            <PaginationSelector itensPerPage={itensPerPage} setItensPerPage={setItensPerPage} />
            <PaginationComponent pages={pages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </Stack>
        )}
      </Box>

      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth maxWidth="md">
        <DialogTitle>{modoEdicao ? "Editar Material" : "Cadastrar Material"}</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Código-base"
                value={codigo}
                onChange={(event) => setCodigo(event.target.value.replace(/\D/g, ""))}
                placeholder={modoEdicao ? "" : "Automático"}
                helperText={
                  modoEdicao
                    ? "Código numérico atual do material."
                    : "Deixe vazio para gerar automaticamente ou informe a base para encaixar um código."
                }
                inputProps={{ inputMode: "numeric" }}
                fullWidth
              />
              <TextField
                label="Complemento do código (opcional)"
                value={complementoCodigo}
                onChange={(event) =>
                  setComplementoCodigo(
                    event.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 5)
                  )
                }
                placeholder="Ex.: B ou 1"
                helperText={
                  codigo && complementoCodigo
                    ? `Código final: ${montarCodigoMaterial(codigo, complementoCodigo)}`
                    : "Use letras ou números, sem digitar o ponto."
                }
                inputProps={{ maxLength: 5 }}
                fullWidth
              />
            </Stack>

                  <Autocomplete
                      options={clientes}
                      getOptionLabel={(option) =>  option?.nome || ""}
                      getOptionKey={(option) => option.id}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={
                          clienteId
                            ? clientes.find(c => c.id === clienteId) || null
                            : null
                        }
                      onChange={async (event, value) => {
                          setCliente(value?.nome || "");
                          setClienteId(value?.id || "");
                          setObraId("");

                          if (value?.id) {
                            await carregarObras(value.id);
                          } else {
                            setObras([]);
                          }
                        }}
                      renderInput={(params) => (
                        <TextField {...params} label="Cliente" required />
                      )}
                    />

                    <Autocomplete
                        options={[OPCAO_TODAS_OBRAS, ...obras]}
                        getOptionLabel={(option) => option?.nome || ""}
                        value={
                          obraId
                            ? [OPCAO_TODAS_OBRAS, ...obras].find(o => o.id === obraId) || null
                            : null
                        }
                        onChange={(event, value) => {
                          setObraId(value?.id || "");
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Obra" required />
                        )}
                      />
                    <TextField
                      label="Fornecedor"
                      value={fornecedor}
                      onChange={(e) => setFornecedor(e.target.value)}
                      required
                    />

                    <TextField
                      label="Local da Coleta / Jazida"
                      value={localColeta}
                      onChange={(e) => setLocalColeta(e.target.value)}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={entregueCliente}
                          onChange={(e) => setEntregueCliente(e.target.checked)}
                        />
                      }
                      label="Material entregue pelo cliente"
                    />

                    {!entregueCliente && (
                      <TextField
                        label="Colaborador que realizou a coleta"
                        value={colaboradorColeta}
                        onChange={(e) => setColaboradorColeta(e.target.value)}
                        required
                      />
                    )}
                    <TextField
                      select
                      label="Categoria do material"
                      value={categoriaMaterial}
                      onChange={(e) => selecionarCategoriaMaterial(e.target.value)}
                      required
                    >
                      {CATEGORIAS_MATERIAL.map((categoria) => (
                        <MenuItem key={categoria.value} value={categoria.value}>{categoria.label}</MenuItem>
                      ))}
                    </TextField>

                    {categoriaMaterial === "concreto" && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
                        <FormControlLabel control={<Checkbox checked={isCimento} onChange={(e) => selecionarTipoMaterial("cimento", e.target.checked)} />} label="Cimento" />
                        <FormControlLabel control={<Checkbox checked={isAditivo} onChange={(e) => selecionarTipoMaterial("aditivo", e.target.checked)} />} label="Aditivo" />
                        <FormControlLabel control={<Checkbox checked={isAdicao} onChange={(e) => selecionarTipoMaterial("adicao", e.target.checked)} />} label="Adição" />
                        <FormControlLabel control={<Checkbox checked={isAgua} onChange={(e) => selecionarTipoMaterial("agua", e.target.checked)} />} label="Água" />
                      </Stack>
                    )}

                    {categoriaMaterial === "agregado" && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <FormControlLabel control={<Checkbox checked={isAgregadoGraudo} onChange={(e) => selecionarTipoMaterial("agregadoGraudo", e.target.checked)} />} label="Agregado graúdo" />
                        <FormControlLabel control={<Checkbox checked={isAgregadoMiudo} onChange={(e) => selecionarTipoMaterial("agregadoMiudo", e.target.checked)} />} label="Agregado miúdo" />
                      </Stack>
                    )}

                    {categoriaMaterial === "asfalto" && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <FormControlLabel control={<Checkbox checked={isCAP} onChange={(e) => selecionarTipoMaterial("cap", e.target.checked)} />} label="CAP" />
                        <FormControlLabel control={<Checkbox checked={isEmulsao} onChange={(e) => selecionarTipoMaterial("emulsao", e.target.checked)} />} label="Emulsão" />
                        <FormControlLabel control={<Checkbox checked={isDOP} onChange={(e) => selecionarTipoMaterial("dop", e.target.checked)} />} label="DOP" />
                      </Stack>
                    )}

                    {categoriaMaterial === "solos" && <Typography variant="body2" color="text.secondary">Material classificado como Solos.</Typography>}

                    {categoriaMaterial === "diversos" && (
                      <Typography variant="body2" color="text.secondary">
                        Categoria destinada a blocos, tijolos, telhas e outros materiais.
                      </Typography>
                    )}

                    {(isCimento || isAditivo || isAdicao || isAgua) && (
                      <TextField
                        label="Massa específica (kg/dm³)"
                        value={massaEspecifica}
                        onChange={(e) => setMassaEspecifica(e.target.value)}
                      />
                    )}
            <TextField
                label="Data"
                type="date"
                required
                value={dataCadastro}
                onChange={e => setDataCadastro(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {(isAgregadoGraudo || isAgregadoMiudo) && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setNovoAgregado("");
                    setPopupTipoAgregado(isAgregadoGraudo ? "graudo" : "miudo");
                  }}
                  sx={{ minWidth: 210 }}
                >
                  {opcaoDescricaoAgregado || "Selecionar classificação"}
                </Button>
              )}
              <TextField
                label="Descrição"
                required
                value={descricao}
                onChange={(e) => atualizarDescricaoMaterial(e.target.value)}
                fullWidth
              />
            </Stack>

            <Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <Button component="label" variant="outlined" disabled={salvando}>
                  Adicionar imagens
                  <input hidden type="file" accept="image/*" multiple onChange={selecionarImagensMaterial} />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {imagensMaterial.length ? `${imagensMaterial.length} imagem(ns) selecionada(s)` : "Nenhuma imagem selecionada"}
                </Typography>
              </Stack>

              {imagensMaterial.length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5, mt: 1.5 }}>
                  {imagensMaterial.map((imagem, index) => (
                    <Paper key={imagem.id || index} variant="outlined" sx={{ p: 1 }}>
                      <Box component="img" src={imagem.previewUrl || imagem.url} alt={imagem.nome || `Imagem ${index + 1}`} sx={{ width: "100%", height: 130, objectFit: "contain", bgcolor: "#f8fafc" }} />
                      <Button size="small" color="error" onClick={() => removerImagemMaterial(index)} disabled={salvando} sx={{ mt: 0.5 }}>
                        Remover
                      </Button>
                    </Paper>
                  ))}
                </Box>
              )}

              {salvando && imagensMaterial.some((imagem) => imagem.arquivo) && (
                <Box sx={{ mt: 1.5 }}>
                  <LinearProgress variant="determinate" value={progressoImagens} />
                  <Typography variant="caption" color="text.secondary">Salvando imagens... {progressoImagens}%</Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        {avisoAprovacao && (
              <Alert severity="info">
                {avisoAprovacao}
              </Alert>
            )}
            {erro && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {erro}
              </Alert>
            )}


        <DialogActions>
            {avisoAprovacao ? (
              <Button
              size="small"
                variant="contained"
                onClick={() => {
                  setShowModal(false);
                  carregar(currentPage); // 🔥 força refresh
                }}
              >
                Fechar
              </Button>

            ) : (
              <>
                <Button onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={salvar}
                  disabled={salvando}
                >
                  {salvando ? "Salvando..." : "Salvar"}
                </Button>
              </>
            )}
          </DialogActions>

      </Dialog>

      <Dialog
        open={Boolean(popupTipoAgregado)}
        onClose={() => setPopupTipoAgregado("")}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {popupTipoAgregado === "graudo"
            ? "Selecione o agregado graúdo"
            : "Selecione o agregado miúdo"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {opcaoDescricaoAgregado &&
              !(popupTipoAgregado === "graudo"
                ? OPCOES_AGREGADO_GRAUDO
                : OPCOES_AGREGADO_MIUDO
              ).includes(opcaoDescricaoAgregado) && (
                <Button variant="contained" onClick={() => setPopupTipoAgregado("")}>
                  {opcaoDescricaoAgregado}
                </Button>
              )}
            {(popupTipoAgregado === "graudo"
              ? OPCOES_AGREGADO_GRAUDO
              : OPCOES_AGREGADO_MIUDO
            ).map((opcao) => (
              <Button
                key={opcao}
                variant={opcaoDescricaoAgregado === opcao ? "contained" : "outlined"}
                onClick={() => selecionarOpcaoDescricaoAgregado(opcao)}
              >
                {opcao}
              </Button>
            ))}
            <Box sx={{ pt: 2, mt: 1, borderTop: "1px solid", borderColor: "divider" }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label={
                    popupTipoAgregado === "graudo"
                      ? "Novo agregado graúdo"
                      : "Novo agregado miúdo"
                  }
                  value={novoAgregado}
                  onChange={(e) => setNovoAgregado(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      adicionarAgregadoManual();
                    }
                  }}
                  size="small"
                  fullWidth
                />
                <Button
                  variant="contained"
                  disabled={!novoAgregado.trim()}
                  onClick={adicionarAgregadoManual}
                  sx={{ minWidth: 175 }}
                >
                  Adicionar agregado
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupTipoAgregado("")}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showImagens} onClose={() => setShowImagens(false)} fullWidth maxWidth="md">
        <DialogTitle>Imagens do material</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
            {imagensVisualizacao.map((imagem, index) => (
              <Box component="img" key={imagem.id || index} src={imagem.url} alt={imagem.nome || `Imagem ${index + 1}`} sx={{ width: "100%", maxHeight: 420, objectFit: "contain", bgcolor: "#f8fafc" }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setShowImagens(false)}>Fechar</Button></DialogActions>
      </Dialog>

      <HistoricoMaterial
        open={showHistorico}
        onClose={() => setShowHistorico(false)}
        material={materialHistorico}
      />
      <TutorialCadastro
        open={openTutorial}
        onClose={() => setOpenTutorial(false)}
        titulo="Tutorial — Cadastro de Materiais"
        etapas={ETAPAS_TUTORIAL_MATERIAIS}
      />
    </>
  );
}
